import Fuse from "fuse.js";

const SNIPPET_PADDING = 40;

const buildSnippet = (text, indices) => {
  const [start, end] = indices[0];
  const snippetStart = Math.max(0, start - SNIPPET_PADDING);
  const snippetEnd = Math.min(text.length, end + 1 + SNIPPET_PADDING);

  let snippet = text.slice(snippetStart, snippetEnd).trim();
  if (snippetStart > 0) snippet = `…${snippet}`;
  if (snippetEnd < text.length) snippet = `${snippet}…`;
  return snippet;
};

// Picks the best matched field (title > keywords > description) and renders
// a display-friendly "match" string for it, mirroring the old extractText
// snippet behavior but driven by Fuse's match indices instead of indexOf.
// `matches` always has at least one entry for a key we searched on, since
// Fuse only returns a result when something matched.
const buildMatch = (item, matches) => {
  const byField = (key) => matches.find((m) => m.key === key);

  if (byField("title")) return item.title;
  if (byField("keywords")) return item.keywords.join(", ");

  const descriptionMatch = byField("description");
  return buildSnippet(item.description, descriptionMatch.indices);
};

const search = (query, items, keys) => {
  if (!query || !items || items.length === 0) return [];

  const fuse = new Fuse(items, {
    keys,
    includeScore: true,
    includeMatches: true,
    threshold: 0.35,
    ignoreLocation: true,
    minMatchCharLength: 2,
    useExtendedSearch: true,
  });

  return fuse.search(query).map(({ item, score, matches }) => ({
    id: item.id,
    title: item.title,
    match: buildMatch(item, matches),
    score,
  }));
};

export const searchBooks = (query, books) => {
  return search(query, books, [
    { name: "title", weight: 0.5 },
    { name: "keywords", weight: 0.3 },
    { name: "description", weight: 0.2 },
  ]).map((result) => ({
    ...result,
    book: books.find((b) => b.id === result.id),
    type: "book",
  }));
};

export const searchSeries = (query, seriesList) => {
  return search(query, seriesList, [
    { name: "title", weight: 0.6 },
    { name: "keywords", weight: 0.3 },
    { name: "description", weight: 0.1 },
  ]).map((result) => ({
    ...result,
    series: seriesList.find((s) => s.id === result.id),
    type: "series",
  }));
};

// Tight-threshold, title-only lookup used by the chatbot to resolve "which
// book is this message about" without requiring the full title as a substring.
export const findBookByTitle = (query, books) => {
  if (!query || !books || books.length === 0) return undefined;

  const fuse = new Fuse(books, {
    keys: ["title"],
    includeScore: true,
    threshold: 0.25,
    ignoreLocation: true,
    minMatchCharLength: 2,
  });

  const [best] = fuse.search(query);
  return best?.item;
};
